/**
 * Parser für das Lead Sheet.
 *
 * Es gibt bewusst keine Sonderzeichen, die man lernen muss. Man schreibt,
 * was man auch von Hand aufs Blatt schreiben würde, und die App erkennt
 * selbst, was gemeint ist:
 *
 *   Intro: ebm, Eb Eb, dm7, D,     Taktraster, Komma trennt die Takte
 *   Vamp: Am,, Dm, G,              zwei Kommas: Akkord in denselben Takt
 *   Prechorus: F#m, E,   4x        Wiederholung am Zeilenende
 *   Instr.: , , , ,                leere Takte
 *
 *   Ebm        Ab                  Akkordzeile ...
 *   Montag früh im Proberaum       ... über der Textzeile darunter
 *
 *   Solo:                          Zeile nur mit Beschriftung
 *   Zwei Chorusse, dann Thema      reiner Text
 *
 * Zusätzlich versteht der Parser weiterhin das ChordPro-Format mit eckigen
 * und geschweiften Klammern, damit ältere Songs unverändert weiterlaufen.
 * Der Parser kommt ohne DOM aus und ist deshalb einzeln testbar.
 */
import {
  chordPositions,
  isChordLine,
  normalizeChord,
  splitBars,
  splitRepeat,
} from './chords.ts';

export type SectionKind = 'none' | 'verse' | 'chorus' | 'bridge' | 'tab';

/** Ein Akkord mit dem Text, der ab dieser Stelle gesungen wird. */
export interface ChordPair {
  /** null = Text vor dem ersten Akkord der Zeile. */
  chord: string | null;
  text: string;
}

/**
 * Eine Taktrasterzeile, wie man sie von Hand aufs Blatt schreibt:
 *
 *   Intro: | F#m | - | E | ~ |     oder     Intro: F#m, -, E, ~,
 *
 * label  = Beschriftung links (nur in der ersten Zeile eines Abschnitts)
 * bars   = ein Eintrag je Takt, darin die Akkorde dieses Takts
 * repeat = Text rechts daneben, z. B. "4x"
 */
export interface GridLine {
  kind: 'grid';
  section: SectionKind;
  label: string | null;
  bars: string[][];
  repeat: string | null;
}

export type SheetLine =
  | GridLine
  | { kind: 'row'; section: SectionKind; pairs: ChordPair[]; chordsOnly: boolean }
  | { kind: 'comment'; section: SectionKind; text: string }
  | { kind: 'label'; section: SectionKind; text: string }
  | { kind: 'spacer'; section: SectionKind };

export interface ParsedSong {
  /** Alle {directive: value} in Kleinschreibung, Langform als Schluessel. */
  meta: Record<string, string>;
  lines: SheetLine[];
}

/* ------------------------------------------------ ChordPro-Verträglichkeit */

/** Kurzformen auf Langformen abbilden. */
const ALIASES: Record<string, string> = {
  t: 'title',
  st: 'subtitle',
  a: 'artist',
  c: 'comment',
  ci: 'comment',
  soc: 'start_of_chorus',
  eoc: 'end_of_chorus',
  sov: 'start_of_verse',
  eov: 'end_of_verse',
  sob: 'start_of_bridge',
  eob: 'end_of_bridge',
  sot: 'start_of_tab',
  eot: 'end_of_tab',
};

const SECTION_START: Record<string, SectionKind> = {
  start_of_chorus: 'chorus',
  start_of_verse: 'verse',
  start_of_bridge: 'bridge',
  start_of_tab: 'tab',
};

const SECTION_END = new Set(['end_of_chorus', 'end_of_verse', 'end_of_bridge', 'end_of_tab']);

const SECTION_LABEL: Record<SectionKind, string> = {
  none: '',
  verse: 'Vers',
  chorus: 'Refrain',
  bridge: 'Bridge',
  tab: 'Tab',
};

/** Zerlegt eine Zeile im alten Format [Dm7]Text in Akkord/Text-Paare. */
export function parseLine(raw: string): ChordPair[] {
  const pairs: ChordPair[] = [];
  let i = 0;
  let text = '';
  let chord: string | null = null;

  const flush = () => {
    if (chord !== null || text.length > 0) pairs.push({ chord, text });
  };

  while (i < raw.length) {
    if (raw[i] === '[') {
      const close = raw.indexOf(']', i + 1);
      if (close === -1) {
        text += raw.slice(i);
        break;
      }
      flush();
      chord = raw.slice(i + 1, close).trim();
      text = '';
      i = close + 1;
      continue;
    }
    text += raw[i];
    i += 1;
  }
  flush();
  return pairs;
}

/* ----------------------------------------------------------- Taktraster */

/**
 * Erkennt eine Taktrasterzeile mit senkrechten Strichen und zerlegt sie.
 * Liefert null, wenn die Zeile keine ist.
 */
export function parseGridLine(
  raw: string,
): { label: string | null; bars: string[][]; repeat: string | null } | null {
  const line = raw.trim();
  if (line === '') return null;

  let label: string | null = null;
  let rest = line;

  const colon = line.indexOf(':');
  if (colon > 0 && colon < 40) {
    const after = line.slice(colon + 1).trim();
    if (after.startsWith('|')) {
      label = line.slice(0, colon).trim();
      rest = after;
    }
  }
  if (!rest.startsWith('|')) return null;

  const lastPipe = rest.lastIndexOf('|');
  if (lastPipe === 0) return null;

  const inner = rest.slice(0, lastPipe + 1);
  const tail = rest.slice(lastPipe + 1).trim();

  const cells = inner.split('|').slice(1, -1);
  const bars = cells.map((cell) =>
    cell.trim().split(/\s+/).filter(Boolean).map(normalizeChord),
  );

  return { label, bars, repeat: tail === '' ? null : tail };
}

/* ------------------------------------------------------ Zeilenerkennung */

/** Trennt eine freiwillige Beschriftung am Zeilenanfang ab. */
function splitLabel(line: string): { label: string | null; rest: string } {
  const colon = line.indexOf(':');
  if (colon <= 0 || colon > 40) return { label: null, rest: line };
  return { label: line.slice(0, colon).trim(), rest: line.slice(colon + 1).trim() };
}

/** Ist die Zeile reiner Gesangstext, also Kandidat für "Akkorde darüber"? */
function isLyricLine(raw: string | undefined): boolean {
  if (raw === undefined) return false;
  const line = raw.trim();
  if (line === '') return false;
  if (line.startsWith('#') || line.startsWith('{')) return false;
  if (line.includes('|')) return false;
  if (isChordLine(line)) return false;
  // Eine reine Beschriftung ("Solo:") ist kein Gesangstext.
  if (/^[^:]{1,40}:$/.test(line)) return false;
  return true;
}

/**
 * Baut aus einer Akkordzeile und der Textzeile darunter die Paare,
 * indem die Spalte jedes Akkords im Text gesucht wird.
 */
export function pairsFromAlignedLines(chordLine: string, lyricLine: string): ChordPair[] {
  const positions = chordPositions(chordLine);
  if (positions.length === 0) return [{ chord: null, text: lyricLine }];

  const pairs: ChordPair[] = [];
  const first = positions[0] as { column: number };
  if (first.column > 0) pairs.push({ chord: null, text: lyricLine.slice(0, first.column) });

  positions.forEach((position, index) => {
    const next = positions[index + 1];
    const end = next ? next.column : Math.max(lyricLine.length, position.column);
    pairs.push({ chord: position.chord, text: lyricLine.slice(position.column, end) });
  });

  // Führende Leerzeichen an den vorigen Abschnitt abgeben, damit der Akkord
  // direkt über dem Wort steht und nicht über der Lücke davor.
  for (let i = 1; i < pairs.length; i += 1) {
    const current = pairs[i] as ChordPair;
    const leading = /^\s+/.exec(current.text);
    if (!leading) continue;
    const previous = pairs[i - 1] as ChordPair;
    previous.text += leading[0];
    current.text = current.text.slice(leading[0].length);
  }
  return pairs;
}

/* --------------------------------------------------------------- Parser */

export function parseChordPro(source: string): ParsedSong {
  const meta: Record<string, string> = {};
  const lines: SheetLine[] = [];
  let section: SectionKind = 'none';

  const raw = source.split(/\r?\n/);

  for (let index = 0; index < raw.length; index += 1) {
    const line = (raw[index] as string).replace(/\s+$/, '');
    const trimmed = line.trim();

    if (trimmed.startsWith('#')) continue;

    if (trimmed === '') {
      lines.push({ kind: 'spacer', section });
      continue;
    }

    /* --- Altes ChordPro-Format: {directive} --- */
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      const inner = trimmed.slice(1, -1).trim();
      const colon = inner.indexOf(':');
      const rawName = (colon === -1 ? inner : inner.slice(0, colon)).trim().toLowerCase();
      const value = colon === -1 ? '' : inner.slice(colon + 1).trim();
      const name = ALIASES[rawName] ?? rawName;

      if (name in SECTION_START) {
        section = SECTION_START[name] as SectionKind;
        lines.push({ kind: 'label', section, text: value || SECTION_LABEL[section] });
      } else if (SECTION_END.has(name)) {
        section = 'none';
      } else if (name === 'comment') {
        lines.push({ kind: 'comment', section, text: value });
      } else {
        meta[name] = value;
      }
      continue;
    }

    /* --- Taktraster mit senkrechten Strichen --- */
    const pipeGrid = parseGridLine(line);
    if (pipeGrid) {
      lines.push({ kind: 'grid', section, ...pipeGrid });
      continue;
    }

    /* --- Zeile nur mit Beschriftung: "Solo:" --- */
    if (/^[^:]{1,40}:$/.test(trimmed)) {
      lines.push({ kind: 'label', section, text: trimmed.slice(0, -1).trim() });
      continue;
    }

    /* --- Beschriftung plus Akkorde: "Intro: ebm, Eb, ..." --- */
    const withLabel = splitLabel(trimmed);
    if (withLabel.label !== null && withLabel.rest !== '' && isChordLine(withLabel.rest)) {
      const { body, repeat } = splitRepeat(withLabel.rest);
      lines.push({ kind: 'grid', section, label: withLabel.label, bars: splitBars(body), repeat });
      continue;
    }

    /* --- Reine Akkordzeile --- */
    if (isChordLine(trimmed)) {
      const { body, repeat } = splitRepeat(trimmed);

      // Ohne Komma und mit Text darunter: Akkorde gehören über den Text.
      if (!body.includes(',') && repeat === null && isLyricLine(raw[index + 1])) {
        const lyric = raw[index + 1] as string;
        lines.push({
          kind: 'row',
          section,
          pairs: pairsFromAlignedLines(line, lyric),
          chordsOnly: false,
        });
        index += 1;
        continue;
      }

      lines.push({ kind: 'grid', section, label: null, bars: splitBars(body), repeat });
      continue;
    }

    /* --- Alles Übrige ist Text, ggf. im alten Format mit [Akkorden] --- */
    const pairs = parseLine(line);
    const chordsOnly = pairs.every((pair) => pair.text.trim() === '');
    lines.push({ kind: 'row', section, pairs, chordsOnly });
  }

  return { meta, lines };
}

/* ------------------------------------------------------------ Auswertung */

/** Zählt die Akkorde in Textzeilen. */
export function countChords(parsed: ParsedSong): number {
  let n = 0;
  for (const line of parsed.lines) {
    if (line.kind !== 'row') continue;
    for (const pair of line.pairs) if (pair.chord) n += 1;
  }
  return n;
}

/** Liest die Zahl aus einer Wiederholungsangabe wie "4x" oder "x4". */
export function repeatCount(repeat: string | null): number {
  if (!repeat) return 1;
  const match = /(\d+)/.exec(repeat);
  if (!match) return 1;
  const value = Number(match[1]);
  return value > 0 ? value : 1;
}

/**
 * Zählt die Takte im Taktraster, Wiederholungen eingerechnet.
 * Das ist die genaueste Grundlage für die Auto-Scroll-Schätzung.
 */
export function countBars(parsed: ParsedSong): number {
  let n = 0;
  for (const line of parsed.lines) {
    if (line.kind !== 'grid') continue;
    n += line.bars.length * repeatCount(line.repeat);
  }
  return n;
}

/** Baut aus den Song-Metadaten die ChordPro-Kopfzeilen (fuer den Export). */
export function metaHeader(meta: Record<string, string>): string {
  return Object.entries(meta)
    .filter(([, v]) => v !== '')
    .map(([k, v]) => `{${k}: ${v}}`)
    .join('\n');
}
